import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import QualificationFormModal from '@/components/QualificationForm';
import {
  ArrowRight, CheckCircle2, ChevronDown, HelpCircle, Users, GraduationCap, Award, CalendarDays,
  ClipboardCheck, UserPlus, Globe, Shield, Eye, BookOpen, KeyRound,
} from 'lucide-react';
import { PLAN_ORDER, publicCatalog } from '@/config/membershipCatalog';
import { useSiteLang } from '@/components/site/SiteLang';

/*
  /como-funciona — sustituye a la antigua /holding-tokenizado (06/10/2026).
  Revisada el 09/10/2026 (Samuel): se recupera el deseo sin lenguaje de inversión.
  Idea fuerza: el mercado inmobiliario que nunca estuvo al alcance de todos, ahora sí;
  acceso a oportunidades reales fuera del mercado; tú eliges en qué participas; aprende
  antes de decidir. Nunca: inversión, rentabilidad, retorno, cifras de resultados, tokens ni FHCT.
*/

const NAVY = '#0a1628';
const GOLD = '#c9a227';

const CAT_ES = publicCatalog('es');
const CAT_EN = publicCatalog('en');

const T = {
  es: {
    steps: [
      { icon: ClipboardCheck, n: '01', t: 'Test de idoneidad y llamada', d: 'Haces el test de idoneidad (menos de 2 minutos) y eliges día y hora para hablar con el equipo. Te conocemos, resolvemos tus dudas y te recomendamos el nivel que encaja contigo. La admisión es por alineación, no por impulso.' },
      { icon: UserPlus, n: '02', t: 'Alta y bienvenida', d: 'Creas tu cuenta en la plataforma, eliges tu membresía y recibes una sesión de bienvenida para orientarte desde el primer día.' },
      { icon: BookOpen, n: '03', t: 'Formación, comunidad y oportunidades', d: 'Accedes a FHC Academy, a las sesiones del club, a la comunidad privada y al área donde se presentan las oportunidades inmobiliarias. Tú decides en cuáles participas.' },
      { icon: Award, n: '04', t: 'Certificación y eventos', d: 'Con Premium vives el Bootcamp Broker Digital presencial y obtienes la certificación privada FHC.' },
    ],
    pillars: [
      { icon: KeyRound, t: 'Acceso', d: 'Oportunidades inmobiliarias reales fuera del mercado, presentadas en el área privada con su documentación y su análisis.' },
      { icon: GraduationCap, t: 'Formación', d: 'FHC Academy te lleva desde cero hasta entender una operación con criterio propio. Aprende antes de decidir.' },
      { icon: Users, t: 'Comunidad', d: 'Un club privado, con admisión por evaluación y una comunidad de miembros y profesionales que comparte criterio.' },
      { icon: CalendarDays, t: 'Eventos y certificación', d: 'Encuentros y bootcamps presenciales y, con Premium, la certificación privada FHC como Broker Digital.' },
    ],
    principles: [
      { icon: Shield, t: 'Admisión por evaluación', d: 'Cada solicitud se revisa. Así la comunidad mantiene su nivel.' },
      { icon: Eye, t: 'Transparencia', d: 'Qué incluye cada nivel, cómo se renueva, cómo se cancela y el período de satisfacción, explicados con claridad antes de que decidas.' },
      { icon: Globe, t: 'Comunidad internacional', d: 'Una comunidad abierta a profesionales de distintos países.' },
      { icon: BookOpen, t: 'Aprende antes de decidir', d: 'Primero te formas. Después eliges tú, con criterio propio, en qué participas. Nadie decide por ti.' },
    ],
    faq: [
      { q: '¿Qué es Freedom Homes Club?', a: 'Un club privado que abre a sus miembros el mercado inmobiliario que antes solo veían unos pocos: acceso a oportunidades reales fuera del mercado, formación para entenderlas, una comunidad privada, eventos y, en el nivel Premium, la certificación privada FHC. El club no te dice qué hacer: te forma para que decidas tú.' },
      { q: '¿Qué tipo de oportunidades hay dentro del club?', a: 'Oportunidades inmobiliarias reales que el equipo y sus colaboradores trabajan fuera de los portales. Se presentan en el área privada con su documentación y su análisis, y cada miembro decide libremente si participa y en cuál, después de formarse. La membresía te da acceso al club; participar es siempre una decisión aparte y voluntaria.' },
      { q: '¿Qué estoy pagando exactamente?', a: CAT_ES.feeScopeNote },
      { q: '¿Cómo se entra en el club?', a: 'Con el test de idoneidad: lo completas en menos de 2 minutos, eliges día y hora para una llamada con el equipo y, si el club encaja contigo, creas tu cuenta y eliges tu membresía.' },
      { q: '¿Cuánto cuesta?', a: CAT_ES.priceAnswer },
      { q: '¿La certificación es oficial?', a: CAT_ES.certNote },
      { q: '¿Puedo cancelar la renovación?', a: 'Sí. La membresía es anual y puedes solicitar que no se renueve. Las condiciones de cancelación están en los Términos y Condiciones.' },
      { q: '¿Hay período de satisfacción?', a: CAT_ES.satisfactionNote },
    ],
    heroBadge: 'Cómo funciona el club',
    heroTitle: 'El mercado inmobiliario que nunca estuvo al alcance de todos.',
    heroAccent: 'Ahora sí.',
    heroDesc: 'Freedom Homes Club reúne a empresarios y profesionales que quieren acceder a oportunidades inmobiliarias reales fuera del mercado, entenderlas a fondo y decidir con criterio propio. Aprendes, accedes y eliges en qué participas, acompañado por una comunidad que comparte criterio.',
    whyLabel: 'Por qué existe',
    whyTitle: 'Las mejores operaciones nunca llegan a un portal.',
    whyAccent: 'Se cierran antes, entre pocos.',
    whyP1: 'Durante décadas, el inmobiliario de calidad ha pedido contactos, conocimiento y tiempo. Lo que llega a los portales es lo que el mercado ya ha filtrado. A la vez, la digitalización y la tokenización están cambiando cómo se estructura y se gestiona la propiedad.',
    whyP2: 'Freedom Homes Club existe para abrir esa puerta: acceso a oportunidades reales, formación para entenderlas con método y una comunidad con la que decidir con criterio propio.',
    imgAlt: 'Villa de la Costa Blanca que ha acogido encuentros presenciales del club',
    imgCaption: 'La comunidad también se encuentra en persona:',
    imgCaptionAccent: 'encuentros y bootcamps en la Costa Blanca.',
    pillarsLabel: 'Qué es el club',
    pillarsTitle: 'Cuatro pilares.',
    pillarsAccent: 'Un solo club.',
    pillarsDesc: 'Aprendes, accedes y decides. Tú eliges en qué participas.',
    stepsLabel: 'Paso a paso',
    stepsTitle: 'Así se entra en Freedom Homes Club',
    plansLabel: 'Membresías',
    plansTitle: 'Tres niveles de pertenencia',
    planCardLabel: 'Membresía',
    planMore: 'Ver qué incluye',
    principlesLabel: 'Cómo trabajamos',
    principlesTitle: 'Principios del club',
    faqLabel: 'FAQ',
    faqTitle: 'Preguntas frecuentes',
    finalTitle: 'El primer paso es',
    finalAccent: 'conocernos.',
    finalDesc: 'El test tarda menos de 2 minutos. Después eliges día y hora para hablar con el equipo. Sin compromiso.',
  },
  en: {
    steps: [
      { icon: ClipboardCheck, n: '01', t: 'Suitability test and call', d: 'Take the suitability test (under 2 minutes) and choose a day and time to speak with the team. We get to know you, answer your questions and recommend the tier that suits you. Admission is about alignment, not impulse.' },
      { icon: UserPlus, n: '02', t: 'Sign-up and welcome', d: 'Create your account on the platform, choose your membership and join a welcome session that gets you oriented from day one.' },
      { icon: BookOpen, n: '03', t: 'Training, community and opportunities', d: 'Get access to FHC Academy, the club’s sessions, the private community and the area where property opportunities are presented. You decide which ones to take part in.' },
      { icon: Award, n: '04', t: 'Certification and events', d: 'With Premium, you attend the in-person Digital Broker Bootcamp and earn the private FHC certification.' },
    ],
    pillars: [
      { icon: KeyRound, t: 'Access', d: 'Real off-market property opportunities, presented in the members’ area with their documentation and analysis.' },
      { icon: GraduationCap, t: 'Training', d: 'FHC Academy takes you from scratch to assessing a property deal with your own judgement. Learn before you decide.' },
      { icon: Users, t: 'Community', d: 'A private club with admission by assessment, and a like-minded community of members and professionals.' },
      { icon: CalendarDays, t: 'Events and certification', d: 'In-person meet-ups and bootcamps and, with Premium, the private FHC certification as a Digital Broker.' },
    ],
    principles: [
      { icon: Shield, t: 'Admission by assessment', d: 'Every application is reviewed. That’s how the community keeps its standard.' },
      { icon: Eye, t: 'Transparency', d: 'What each tier includes, how it renews, how to cancel and how the satisfaction period works: all explained clearly before you decide.' },
      { icon: Globe, t: 'An international community', d: 'A community open to professionals from different countries.' },
      { icon: BookOpen, t: 'Learn before you decide', d: 'Training comes first. Then you choose, with your own judgement, what to take part in. No one decides for you.' },
    ],
    faq: [
      { q: 'What is Freedom Homes Club?', a: 'A private club that opens up to its members a property market that only a few used to see: access to real off-market opportunities, training to understand them, a private community, events and, at Premium tier, the private FHC certification. The club doesn’t tell you what to do. It trains you to decide for yourself.' },
      { q: 'What kind of opportunities are there inside the club?', a: 'Real property opportunities that the team and its partners work on away from the property portals. They are presented in the members’ area with their documentation and analysis, and each member decides freely whether to take part, and in which, once they have done the training. Membership gives you access to the club; taking part is always a separate, voluntary decision.' },
      { q: 'What exactly am I paying for?', a: CAT_EN.feeScopeNote },
      { q: 'How do I join the club?', a: 'Through the suitability test: it takes under 2 minutes, then you choose a day and time for a call with the team and, if the club is right for you, you create your account and choose your membership.' },
      { q: 'How much does it cost?', a: CAT_EN.priceAnswer },
      { q: 'Is the certification official?', a: CAT_EN.certNote },
      { q: 'Can I cancel the renewal?', a: 'Yes. Membership is annual and you can ask for it not to renew. The cancellation terms are set out in the Terms and Conditions.' },
      { q: 'Is there a satisfaction period?', a: CAT_EN.satisfactionNote },
    ],
    heroBadge: 'How the club works',
    heroTitle: 'The property market that was never within everyone’s reach.',
    heroAccent: 'Now it is.',
    heroDesc: 'Freedom Homes Club brings together entrepreneurs and professionals who want access to real off-market property opportunities, to understand them in depth and to decide with their own judgement. You learn, you gain access and you choose what to take part in, alongside a like-minded community.',
    whyLabel: 'Why it exists',
    whyTitle: 'The best deals never reach a portal.',
    whyAccent: 'They’re closed first, among a few.',
    whyP1: 'For decades, quality property has demanded contacts, knowledge and time. What reaches the portals is what the market has already passed over. At the same time, digitalisation and tokenisation are changing how property is structured and managed.',
    whyP2: 'Freedom Homes Club exists to open that door: access to real opportunities, training to understand them with method, and a community to help you decide with your own judgement.',
    imgAlt: 'A Costa Blanca villa that has hosted the club’s in-person meet-ups',
    imgCaption: 'The community also meets in person:',
    imgCaptionAccent: 'meet-ups and bootcamps on the Costa Blanca.',
    pillarsLabel: 'What the club is',
    pillarsTitle: 'Four pillars.',
    pillarsAccent: 'One club.',
    pillarsDesc: 'You learn, you gain access, you decide. You choose what to take part in.',
    stepsLabel: 'Step by step',
    stepsTitle: 'How to join Freedom Homes Club',
    plansLabel: 'Memberships',
    plansTitle: 'Three tiers of membership',
    planCardLabel: 'Membership',
    planMore: 'See what’s included',
    principlesLabel: 'How we work',
    principlesTitle: 'The club’s principles',
    faqLabel: 'FAQ',
    faqTitle: 'Frequently asked questions',
    finalTitle: 'The first step is',
    finalAccent: 'getting to know each other.',
    finalDesc: 'The test takes under 2 minutes. Then you choose a day and time to speak with the team. No commitment.',
  },
};

function FAQItem({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200" data-testid={`faq-${idx}`}>
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 p-5 sm:p-6 text-left">
        <span className="flex items-start gap-3">
          <HelpCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: GOLD }} />
          <span className="font-heading text-base sm:text-lg text-[#0a1628]">{q}</span>
        </span>
        <ChevronDown className="w-4 h-4 flex-shrink-0 text-slate-400 transition-transform" style={{ transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>
      {open && <p className="px-5 sm:px-6 pb-6 pl-[52px] sm:pl-14 font-body text-slate-600 leading-relaxed">{a}</p>}
    </div>
  );
}

export default function ComoFunciona() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const open = () => setIsModalOpen(true);
  const { lang } = useSiteLang();
  const t = T[lang] || T.es;
  const cat = publicCatalog(lang);

  return (
    <div className="min-h-screen bg-[#fafbfc]">

      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center" style={{ background: NAVY }} data-testid="hero-section">
        <div className="relative z-10 max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-32 pb-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 border px-4 py-2 mb-8" style={{ borderColor: `${GOLD}4d`, background: `${GOLD}1a` }}>
              <div className="w-2 h-2 rounded-full" style={{ background: GOLD }} />
              <span className="font-body text-xs uppercase tracking-widest" style={{ color: GOLD }}>{t.heroBadge}</span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-medium text-white leading-tight mb-6" data-testid="hero-headline">
              {t.heroTitle} <span style={{ color: GOLD }}>{t.heroAccent}</span>
            </h1>
            <p className="font-body text-lg text-white/65 max-w-2xl mb-10 leading-relaxed">
              {t.heroDesc}
            </p>
            <Button onClick={open} className="h-14 px-6 sm:px-8 font-semibold tracking-wide rounded-none inline-flex items-center gap-2 hover:brightness-110"
              style={{ background: GOLD, color: NAVY }} data-testid="hero-cta-primary">
              {cat.testCta} <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:block"><ChevronDown className="w-6 h-6 text-white/30 animate-bounce" /></div>
      </section>

      {/* El cambio */}
      <section className="py-24 bg-white" data-testid="change-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          <div>
            <span className="font-body text-xs uppercase tracking-widest mb-4 block" style={{ color: GOLD }}>{t.whyLabel}</span>
            <h2 className="font-heading text-3xl md:text-4xl text-[#0a1628] mb-6 leading-tight">
              {t.whyTitle} <span className="italic" style={{ color: GOLD }}>{t.whyAccent}</span>
            </h2>
            <p className="font-body text-slate-600 mb-4 leading-relaxed">
              {t.whyP1}
            </p>
            <p className="font-body text-slate-600 leading-relaxed">
              {t.whyP2}
            </p>
          </div>
          <div className="relative h-[340px] md:h-[420px] overflow-hidden">
            <img src="/media/amaroo-piscina.jpg" alt={t.imgAlt} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628]/70 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 sm:right-6 bg-white/95 p-4">
              <p className="font-body text-sm text-slate-600">{t.imgCaption} <span className="font-semibold" style={{ color: GOLD }}>{t.imgCaptionAccent}</span></p>
            </div>
          </div>
        </div>
      </section>

      {/* Pilares */}
      <section className="py-24" style={{ background: NAVY }} data-testid="pillars-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="font-body text-xs uppercase tracking-widest mb-4 block" style={{ color: GOLD }}>{t.pillarsLabel}</span>
            <h2 className="font-heading text-3xl md:text-4xl text-white">{t.pillarsTitle} <span className="italic" style={{ color: GOLD }}>{t.pillarsAccent}</span></h2>
            <p className="font-body text-white/55 max-w-xl mx-auto mt-4 leading-relaxed">{t.pillarsDesc}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.pillars.map((p) => (
              <div key={p.t} className="border border-white/10 p-6">
                <p.icon className="w-6 h-6 mb-4" style={{ color: GOLD }} />
                <h3 className="font-heading text-lg text-white mb-2">{p.t}</h3>
                <p className="font-body text-sm text-white/55 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pasos */}
      <section className="py-24 bg-white" data-testid="steps-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="font-body text-xs uppercase tracking-widest mb-4 block" style={{ color: GOLD }}>{t.stepsLabel}</span>
            <h2 className="font-heading text-3xl md:text-4xl text-[#0a1628]">{t.stepsTitle}</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {t.steps.map((s) => (
              <div key={s.n} className="flex gap-4 sm:gap-5 p-5 sm:p-6 border border-slate-100">
                <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center" style={{ background: NAVY }}>
                  <s.icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-body text-xs font-bold tracking-widest mb-1" style={{ color: GOLD }}>{s.n}</p>
                  <h3 className="font-heading text-lg text-[#0a1628] mb-1">{s.t}</h3>
                  <p className="font-body text-sm text-slate-500 leading-relaxed">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Membresías */}
      <section className="py-24 bg-[#f4f6f9]" data-testid="plans-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="font-body text-xs uppercase tracking-widest mb-4 block" style={{ color: GOLD }}>{t.plansLabel}</span>
            <h2 className="font-heading text-3xl md:text-4xl text-[#0a1628]">{t.plansTitle}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {PLAN_ORDER.map((id) => {
              const p = cat.plans[id];
              const featured = id === 'premium';
              return (
                <div key={id} className={`p-7 flex flex-col ${featured ? 'text-white' : 'bg-white'}`}
                     style={featured ? { background: NAVY, border: `2px solid ${GOLD}` } : { border: '1px solid #e2e8f0' }}>
                  <p className="font-body text-xs uppercase tracking-widest mb-2" style={{ color: GOLD }}>{t.planCardLabel}</p>
                  <p className="font-heading text-4xl mb-5">{p.name}</p>
                  <div className="space-y-2 mb-6 flex-1">
                    {p.features.map((f) => (
                      <p key={f} className={`font-body text-sm flex items-start gap-2 ${featured ? 'text-white/75' : 'text-slate-600'}`}>
                        <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: GOLD }} /> {f}
                      </p>
                    ))}
                  </div>
                  <button type="button" onClick={open} data-testid={`plan-cta-${id}`}
                    className="w-full min-h-[48px] px-4 py-3 font-body text-sm font-semibold inline-flex items-center justify-center gap-2 hover:brightness-110"
                    style={featured ? { background: GOLD, color: NAVY } : { border: `2px solid ${NAVY}`, color: NAVY }}>
                    {cat.testCta} <ArrowRight className="w-4 h-4 flex-shrink-0" />
                  </button>
                  <Link to={id === 'starter' ? '/membresias' : `/membresias/${id === 'basica' ? 'pro' : id}`}
                    className="mt-2 min-h-[44px] font-body text-sm font-semibold inline-flex items-center justify-center gap-1.5 hover:underline" style={{ color: GOLD }}>
                    {t.planMore} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              );
            })}
          </div>
          <p className="text-center font-body text-xs text-slate-400 mt-8 leading-relaxed">{cat.bootcampNote}<br />{cat.feeScopeNote}</p>
        </div>
      </section>

      {/* Principios */}
      <section className="py-24 bg-white" data-testid="principles-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="font-body text-xs uppercase tracking-widest mb-4 block" style={{ color: GOLD }}>{t.principlesLabel}</span>
            <h2 className="font-heading text-3xl md:text-4xl text-[#0a1628]">{t.principlesTitle}</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {t.principles.map((p) => (
              <div key={p.t} className="flex gap-4 p-6 bg-[#fafbfc] border border-slate-100">
                <p.icon className="w-6 h-6 flex-shrink-0" style={{ color: GOLD }} />
                <div>
                  <h3 className="font-heading text-lg text-[#0a1628] mb-1">{p.t}</h3>
                  <p className="font-body text-sm text-slate-500 leading-relaxed">{p.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-[#fafbfc]" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="font-body text-xs uppercase tracking-widest mb-4 block" style={{ color: GOLD }}>{t.faqLabel}</span>
            <h2 className="font-heading text-3xl md:text-4xl text-[#0a1628]">{t.faqTitle}</h2>
          </div>
          <div className="space-y-3">
            {t.faq.map((f, i) => <FAQItem key={f.q} q={f.q} a={f.a} idx={i} />)}
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="py-24 text-center" style={{ background: NAVY }} data-testid="final-cta-section">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="font-heading text-3xl md:text-5xl text-white mb-6 leading-tight">
            {t.finalTitle} <span className="italic" style={{ color: GOLD }}>{t.finalAccent}</span>
          </h2>
          <p className="font-body text-lg text-white/60 mb-10">{t.finalDesc}</p>
          <Button onClick={open} className="h-14 px-6 sm:px-10 font-semibold rounded-none inline-flex items-center gap-2 hover:brightness-110"
            style={{ background: GOLD, color: NAVY }} data-testid="final-cta">
            {cat.testCta} <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>

      <QualificationFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} source="como-funciona" lang={lang} />
    </div>
  );
}
